import { getTenantByHost } from '@pelicargo/tenants';
import { useMemo } from 'react';

import { useCustomerTheme } from './useCustomerTheme';

type LogoDims = { width?: number | string; height?: number | string };

/**
 * The logo's intrinsic box, from the DB theme's `logo` overrides falling back to the tenant's
 * static `customerTheme.logoDims` / `fullLogoDims`.
 *
 * Unlike the rest of the theme these are layout applied as React props rather than CSS variables,
 * so this hook re-renders the logo when the theme lands. That only matters once the image has
 * loaded, which is why it is not worth routing through a custom property.
 */
export const useCustomerLogoDims = (fullSized = false): LogoDims => {
  const theme = useCustomerTheme();
  const logo = theme?.logo;
  const staticDims = useMemo(
    () => getTenantByHost().customerTheme?.[fullSized ? 'fullLogoDims' : 'logoDims'],
    [fullSized],
  );

  return useMemo(() => {
    const width = fullSized ? logo?.fullWidth : logo?.width;
    const height = fullSized ? logo?.fullHeight : logo?.height;

    return { width: width ?? staticDims?.width, height: height ?? staticDims?.height };
  }, [fullSized, logo?.fullHeight, logo?.fullWidth, logo?.height, logo?.width, staticDims]);
};
